import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as DB from '@/lib/supabaseDb';
import { useDataRefresh } from '@/hooks/useDataRefresh';
import DeliveryReport from '@/components/feature/DeliveryReport';
import { toTurkishLower } from '@/lib/customerSearch';
import Badge from '@/components/base/Badge';
import { deliveryStatus, getDeliveryLabelByCode } from '@/lib/statusDefs';
import CityDistrictSelect from '@/components/base/CityDistrictSelect';

type DeliveryStatus = 'planned' | 'on_the_way' | 'delivered';
type SortKey = 'customer_name' | 'delivery_date' | 'delivery_start_time' | 'district';
type SortDir = 'asc' | 'desc';
type TabKey = 'all' | 'today' | 'upcoming' | 'delivered';

const STATUS_DISPLAY: Record<string, string> = {
  'planned': 'Planlandı',
  'on_the_way': 'Yolda',
  'delivered': 'Teslim Edildi',
};

const STATUS_COLORS: Record<string, string> = {
  'planned': 'bg-amber-100 text-amber-700',
  'on_the_way': 'bg-purple-100 text-purple-700',
  'delivered': 'bg-emerald-100 text-emerald-700',
};

function getStatusBadge(status: string) {
  const color = STATUS_COLORS[status] || 'bg-background-100 text-foreground-500';
  const label = STATUS_DISPLAY[status] || status;
  return { className: `inline-flex px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${color}`, label };
}

export default function DeliveriesPage() {
  const navigate = useNavigate();
  const { triggerRefresh } = useDataRefresh();
  const [searchParams] = useSearchParams();
  const srcParam = searchParams.get('src');
  const dateParam = searchParams.get('date');
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => { setRefreshKey(k => k + 1); triggerRefresh(); }, [triggerRefresh]);
  const [deliveries, setDeliveries] = useState<any[]>([]);

  useEffect(() => {
    DB.getDeliveries().then(({ data }) => setDeliveries((data || []) as any[]));
  }, [refreshKey]);
  const allSales = useMemo(() => {
    return [];
  }, [refreshKey]);

  const [activeTab, setActiveTab] = useState<TabKey>(dateParam === 'today' ? 'today' : 'all');
  const [search, setSearch] = useState('');
  const [districtFilter, setDistrictFilter] = useState('Tümü');
  const [sortKey, setSortKey] = useState<SortKey>('delivery_date');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState<string | null>(null);
  const [showToast, setShowToast] = useState<string | null>(null);
  const [showReportDelivery, setShowReportDelivery] = useState<any | null>(null);
  const [showDeliveredPrompt, setShowDeliveredPrompt] = useState<any | null>(null);
  const pageSize = 8;

  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });
  const districts = useMemo(() => {
    const d = new Set((deliveries || []).map((d: any) => d.district).filter(Boolean));
    return ['Tümü', ...Array.from(d).sort() as string[]];
  }, [deliveries]);

  // Edit modal state
  const editingDelivery = useMemo(() => {
    if (!showEditModal) return null;
    return (deliveries || []).find((d: any) => d.id === showEditModal) || null;
  }, [showEditModal, deliveries]);

  const [editDate, setEditDate] = useState('');
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');
  const [editPerson, setEditPerson] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editCity, setEditCity] = useState('');
  const [editDistrict, setEditDistrict] = useState('');
  const [editNeighborhood, setEditNeighborhood] = useState('');
  const [editStatus, setEditStatus] = useState<DeliveryStatus>('planned');
  const [editNotes, setEditNotes] = useState('');

  const openEditModal = useCallback((id: string) => {
    const del = (deliveries || []).find((d: any) => d.id === id);
    if (!del) return;
    setEditDate(del.delivery_date || '');
    setEditStartTime(del.delivery_start_time || del.delivery_time || '');
    setEditEndTime(del.delivery_end_time || '');
    setEditPerson(del.delivery_person || '');
    setEditAddress(del.address || '');
    setEditCity(del.city || '');
    setEditDistrict(del.district || '');
    setEditNeighborhood(del.neighborhood || '');
    setEditStatus(del.status || 'planned');
    setEditNotes(del.notes || '');
    setShowEditModal(id);
  }, [deliveries]);

  const handleEditSave = async () => {
    if (!showEditModal || !editingDelivery) return;
    const oldStatus = editingDelivery.status;

    const updates: any = {
      delivery_date: editDate,
      delivery_start_time: editStartTime,
      delivery_end_time: editEndTime,
      delivery_person: editPerson,
      address: editAddress,
      city: editCity,
      district: editDistrict,
      neighborhood: editNeighborhood,
      status: editStatus,
      notes: editNotes,
    };

    if (editStatus === 'delivered' && oldStatus !== 'delivered') {
      updates.delivered_at = new Date().toISOString();
    }

    await DB.updateDelivery(showEditModal, updates);

    // Cascade status to sale with Turkish display values for UI compatibility
    const saleId = editingDelivery.sale_id;
    if (saleId) {
      await DB.updateSale(saleId, { delivery_status: STATUS_DISPLAY[editStatus] || editStatus });
    }

    setShowToast('Teslimat güncellendi.');
    setShowEditModal(null);

    // If status changed to delivered, show print prompt
    if (editStatus === 'delivered' && oldStatus !== 'delivered') {
      const updatedDel = { ...editingDelivery, ...updates };
      setShowDeliveredPrompt(updatedDel);
    }

    refresh();
    setTimeout(() => setShowToast(null), 3000);
  };

  const handleQuickStatusChange = async (id: string, newStatus: string) => {
    const del = (deliveries || []).find((d: any) => d.id === id);
    if (!del || del.status === newStatus) return;

    const updates: any = { status: newStatus };
    if (newStatus === 'delivered' && del.status !== 'delivered') {
      updates.delivered_at = new Date().toISOString();
    }

    await DB.updateDelivery(id, updates);

    // Cascade to sale with Turkish display value
    if (del.sale_id) {
      await DB.updateSale(del.sale_id, { delivery_status: STATUS_DISPLAY[newStatus] || newStatus });
    }

    setShowToast(`Teslimat durumu: ${STATUS_DISPLAY[newStatus]}`);
    refresh();
    setTimeout(() => setShowToast(null), 3000);
  };

  const filtered = useMemo(() => {
    let result = [...(deliveries || [])];
    switch (activeTab) {
      case 'today': result = result.filter((d: any) => d.delivery_date === today); break;
      case 'upcoming': result = result.filter((d: any) => d.delivery_date > today && d.status !== 'delivered'); break;
      case 'delivered': result = result.filter((d: any) => d.status === 'delivered'); break;
    }
    if (search) { const q = toTurkishLower(search.trim()); result = result.filter((d: any) => toTurkishLower(d.customer_name || '').includes(q) || toTurkishLower(d.sale_no || '').includes(q) || toTurkishLower(d.product_name || '').includes(q)); }
    if (districtFilter !== 'Tümü') result = result.filter((d: any) => d.district === districtFilter);
    result.sort((a: any, b: any) => {
      let va: any, vb: any;
      if (sortKey === 'delivery_start_time') { va = a.delivery_start_time || a.delivery_time || ''; vb = b.delivery_start_time || b.delivery_time || ''; }
      else { va = a[sortKey]; vb = b[sortKey]; }
      if (typeof va === 'string') return sortDir === 'asc' ? (va || '').localeCompare(vb || '', 'tr') : (vb || '').localeCompare(va || '', 'tr');
      return sortDir === 'asc' ? (va || 0) - (vb || 0) : (vb || 0) - (va || 0);
    });
    return result;
  }, [deliveries, search, districtFilter, sortKey, sortDir, activeTab, today]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleSort = (key: SortKey) => { if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('desc'); } };
  const sortIcon = (key: SortKey) => sortKey !== key ? <i className="ri-arrow-up-down-line text-foreground-300 text-xs"></i> : <i className={`text-xs text-primary-500 ${sortDir === 'asc' ? 'ri-sort-asc' : 'ri-sort-desc'}`}></i>;

  const handleDelete = async () => {
    if (!showDeleteModal) return;
    await DB.deleteDelivery(showDeleteModal);
    setShowToast('Teslimat iptal edildi');
    setShowDeleteModal(null);
    refresh();
    setTimeout(() => setShowToast(null), 3000);
  };

  const handleMarkDelivered = (id: string) => {
    const del = (deliveries || []).find((d: any) => d.id === id);
    if (!del) return;
    setShowDeliveredPrompt(del);
  };

  const confirmMarkDelivered = async () => {
    if (!showDeliveredPrompt) return;
    const del = showDeliveredPrompt;
    await DB.updateDelivery(del.id, { status: 'delivered', delivered_at: new Date().toISOString() });
    if (del.sale_id) await DB.updateSale(del.sale_id, { delivery_status: 'Teslim Edildi' });
    setShowToast('Teslimat tamamlandı olarak işaretlendi');
    setShowDeliveredPrompt(null);
    refresh();
    setTimeout(() => setShowToast(null), 3000);
  };

  const confirmDeliveredAndPrint = async () => {
    if (!showDeliveredPrompt) return;
    const del = showDeliveredPrompt;
    await DB.updateDelivery(del.id, { status: 'delivered', delivered_at: new Date().toISOString() });
    if (del.sale_id) await DB.updateSale(del.sale_id, { delivery_status: 'Teslim Edildi' });
    setShowToast('Teslimat tamamlandı olarak işaretlendi');
    const updatedDel = { ...del, status: 'delivered', delivered_at: new Date().toISOString() };
    setShowDeliveredPrompt(null);
    setShowReportDelivery(updatedDel);
    refresh();
    setTimeout(() => setShowToast(null), 3000);
  };

  const tabs: { key: TabKey; label: string; icon: string }[] = [
    { key: 'all', label: 'Tüm Teslimatlar', icon: 'ri-file-list-3-line' },
    { key: 'today', label: 'Bugün', icon: 'ri-calendar-check-line' },
    { key: 'upcoming', label: 'Yaklaşan', icon: 'ri-time-line' },
    { key: 'delivered', label: 'Teslim Edilen', icon: 'ri-check-double-line' },
  ];

  const todayCount = (deliveries || []).filter((d: any) => d.delivery_date === today).length;
  const upcomingCount = (deliveries || []).filter((d: any) => d.delivery_date > today && d.status !== 'delivered').length;
  const deliveredCount = (deliveries || []).filter((d: any) => d.status === 'delivered').length;

  const inputClass = 'w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-800';
  const selectClass = 'w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-800 cursor-pointer';
  const labelClass = 'text-xs font-medium text-foreground-500 mb-1.5 block';

  if (!deliveries || deliveries.length === 0) {
    return (
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">Teslimatlar</h2>
            <p className="text-sm text-foreground-400 mt-0.5">Toplam 0 teslimat</p>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center">
            <i className="ri-truck-line text-3xl text-foreground-300"></i>
          </div>
          <h3 className="text-lg font-semibold text-foreground-700">Henüz teslimat kaydı yok</h3>
          <p className="text-sm text-foreground-400 max-w-sm text-center">Satış oluşturulduğunda teslimat kayıtları burada görünecek.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {showToast && <div className="fixed top-4 right-4 z-[100] bg-emerald-500 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-2"><i className="ri-check-line"></i><span className="text-sm font-medium">{showToast}</span></div>}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">Teslimatlar</h2>
          <p className="text-sm text-foreground-400 mt-0.5">Teslimat planlaması ve takibi</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="stat-card">
          <p className="text-xs text-foreground-400 mb-1">Bugünkü</p>
          <p className="text-lg font-bold text-primary-600">{todayCount} adet</p>
        </div>
        <div className="stat-card">
          <p className="text-xs text-foreground-400 mb-1">Yaklaşan</p>
          <p className="text-lg font-bold text-amber-600">{upcomingCount} adet</p>
        </div>
        <div className="stat-card">
          <p className="text-xs text-foreground-400 mb-1">Teslim Edilen</p>
          <p className="text-lg font-bold text-emerald-600">{deliveredCount} adet</p>
        </div>
      </div>

      <div className="flex items-center gap-1 p-1 bg-background-100 rounded-full w-fit flex-wrap">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => { setActiveTab(tab.key); setCurrentPage(1); }}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${activeTab === tab.key ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-400 hover:text-foreground-600'}`}
          >
            <i className={tab.icon}></i>
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <input
            type="text" placeholder="Müşteri, satış no ara..." value={search}
            onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
            className="w-full pl-10 pr-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
          />
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-300 text-sm"></i>
        </div>
        <select
          value={districtFilter}
          onChange={e => { setDistrictFilter(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 cursor-pointer"
        >
          {districts.map(d => <option key={d} value={d}>İlçe: {d}</option>)}
        </select>
      </div>

      <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full min-w-[1200px]">
            <thead>
              <tr className="border-b border-background-200 bg-background-100/50">
                <th className="table-header cursor-pointer" onClick={() => handleSort('customer_name')}>Müşteri {sortIcon('customer_name')}</th>
                <th className="table-header">Satış No</th>
                <th className="table-header">Ürün</th>
                <th className="table-header cursor-pointer" onClick={() => handleSort('delivery_date')}>Tarih {sortIcon('delivery_date')}</th>
                <th className="table-header cursor-pointer" onClick={() => handleSort('delivery_start_time')}>Saat {sortIcon('delivery_start_time')}</th>
                <th className="table-header">Nakliyeci</th>
                <th className="table-header cursor-pointer" onClick={() => handleSort('district')}>İlçe {sortIcon('district')}</th>
                <th className="table-header">Durum</th>
                <th className="table-header text-center">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {(paginated || []).map((d: any) => {
                const dispTime = d.delivery_start_time || d.delivery_time || '';
                const rowBg = d.status === 'on_the_way' ? 'bg-purple-50/20' : '';
                const badge = getStatusBadge(d.status);
                return (
                  <tr key={d.id} className={`border-b border-background-100 hover:bg-background-50 ${rowBg}`}>
                    <td className="table-cell">
                      <button onClick={() => navigate(`/customers/${d.customer_id}`)} className="text-foreground-700 hover:text-primary-500 font-medium cursor-pointer">
                        {d.customer_name}
                      </button>
                      <p className="text-[11px] text-foreground-400">{d.customer_phone}</p>
                    </td>
                    <td className="table-cell">
                      <button onClick={() => navigate(`/sales/${d.sale_id}`)} className="text-primary-600 hover:text-primary-700 text-sm cursor-pointer">
                        {d.sale_no}
                      </button>
                    </td>
                    <td className="table-cell text-foreground-500 text-sm max-w-[200px] truncate">{d.product_name}</td>
                    <td className="table-cell text-foreground-500 text-sm">{d.delivery_date ? new Date(d.delivery_date + 'T00:00:00').toLocaleDateString('tr-TR') : ''}</td>
                    <td className="table-cell text-foreground-500 text-sm">{dispTime}</td>
                    <td className="table-cell text-foreground-500 text-sm">{d.delivery_person || '—'}{d.vehicle_plate ? <span className="block text-[11px] text-foreground-300">{d.vehicle_plate}</span> : null}</td>
                    <td className="table-cell text-foreground-500 text-sm">{d.district || '—'}</td>
                    <td className="table-cell py-2">
                      <select
                        value={d.status}
                        onChange={(e) => handleQuickStatusChange(d.id, e.target.value)}
                        className={`text-xs px-2.5 py-1 rounded-full font-medium border-0 outline-none cursor-pointer appearance-none text-center ${badge.className}`}
                        style={{ backgroundImage: 'none' }}
                      >
                        <option value="planned">Planlandı</option>
                        <option value="on_the_way">Yolda</option>
                        <option value="delivered">Teslim Edildi</option>
                      </select>
                    </td>
                    <td className="table-cell py-2">
                      <div className="flex items-center justify-center gap-1.5">
                        <button onClick={() => openEditModal(d.id)} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-primary-100" title="Düzenle">
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button onClick={() => navigate(`/sales/${d.sale_id}`)} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-primary-100" title="Satışa Git">
                          <i className="ri-eye-line text-sm"></i>
                        </button>
                        {d.status !== 'delivered' && (
                          <button onClick={() => handleMarkDelivered(d.id)} className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center cursor-pointer hover:bg-emerald-100" title="Tamamlandı">
                            <i className="ri-check-line text-sm text-emerald-500"></i>
                          </button>
                        )}
                        <button onClick={() => setShowReportDelivery(d)} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-accent-100 hover:text-accent-700" title="Rapor Yazdır">
                          <i className="ri-printer-line text-sm"></i>
                        </button>
                        <button onClick={() => setShowDeleteModal(d.id)} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-red-50 hover:text-red-500" title="Sil">
                          <i className="ri-close-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {paginated.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-16 text-center"><p className="text-sm text-foreground-400">Teslimat bulunamadı</p></td></tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Mobile Cards */}
        <div className="lg:hidden p-3 space-y-2.5">
          {(paginated || []).map((d: any) => {
            const dispTime = d.delivery_start_time || d.delivery_time || '';
            const badge = getStatusBadge(d.status);
            return (
              <div key={d.id} className="bg-background-50 border border-background-200/70 rounded-xl p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge badgeClass={deliveryStatus.badgeClass(getDeliveryLabelByCode(d.status))} icon={deliveryStatus.icon(getDeliveryLabelByCode(d.status))}>{badge.label}</Badge>
                  </div>
                  <select
                    value={d.status}
                    onChange={(e) => handleQuickStatusChange(d.id, e.target.value)}
                    className={`text-xs px-2 py-1 rounded-full font-medium border-0 outline-none cursor-pointer appearance-none text-center ${badge.className}`}
                    style={{ backgroundImage: 'none' }}
                  >
                    <option value="planned">Planlandı</option>
                    <option value="on_the_way">Yolda</option>
                    <option value="delivered">Teslim Edildi</option>
                  </select>
                </div>
                <button onClick={() => navigate(`/customers/${d.customer_id}`)} className="text-sm font-semibold text-foreground-800 block cursor-pointer hover:text-primary-500">
                  {d.customer_name}
                </button>
                <p className="text-xs text-foreground-400">{d.customer_phone}</p>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-foreground-500">{d.delivery_date ? new Date(d.delivery_date + 'T00:00:00').toLocaleDateString('tr-TR') : ''} {dispTime}</span>
                  <span className="text-foreground-500">{d.district || '—'}</span>
                </div>
                <p className="text-xs text-foreground-400 truncate">{d.product_name}</p>
                <div className="flex items-center justify-between pt-1 border-t border-background-100">
                  <span className="text-[11px] text-foreground-400">{d.delivery_person || 'Nakliyeci: —'}</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEditModal(d.id)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-primary-100"><i className="ri-edit-line text-xs"></i></button>
                    <button onClick={() => navigate(`/sales/${d.sale_id}`)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-primary-100"><i className="ri-eye-line text-xs"></i></button>
                    {d.status !== 'delivered' && (
                      <button onClick={() => handleMarkDelivered(d.id)} className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center cursor-pointer hover:bg-emerald-100"><i className="ri-check-line text-xs text-emerald-500"></i></button>
                    )}
                    <button onClick={() => setShowReportDelivery(d)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-accent-100 hover:text-accent-700"><i className="ri-printer-line text-xs"></i></button>
                  </div>
                </div>
              </div>
            );
          })}
          {paginated.length === 0 && (
            <div className="flex flex-col items-center gap-3 py-10">
              <div className="w-12 h-12 rounded-xl bg-background-100 flex items-center justify-center"><i className="ri-truck-line text-xl text-foreground-300"></i></div>
              <p className="text-sm text-foreground-400">Teslimat bulunamadı</p>
            </div>
          )}
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-background-200">
            <span className="text-xs text-foreground-400">Sayfa {currentPage} / {totalPages}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer hover:bg-background-100 disabled:opacity-30">
                <i className="ri-arrow-left-s-line text-sm"></i>
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} onClick={() => setCurrentPage(p)} className={`w-8 h-8 rounded-lg text-sm font-medium cursor-pointer ${p === currentPage ? 'bg-primary-500 text-background-50' : 'hover:bg-background-100 text-foreground-600'}`}>{p}</button>
              ))}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer hover:bg-background-100 disabled:opacity-30">
                <i className="ri-arrow-right-s-line text-sm"></i>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/40 z-[100] flex items-center justify-center p-4" onClick={() => setShowDeleteModal(null)}>
          <div className="bg-background-50 rounded-2xl p-6 max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4 mx-auto">
              <i className="ri-error-warning-line text-xl text-red-500"></i>
            </div>
            <h3 className="text-lg font-semibold text-foreground-900 text-center mb-2">Teslimatı Sil</h3>
            <p className="text-sm text-foreground-500 text-center mb-6">Bu teslimat kaydı silinecek. Bu işlem geri alınamaz.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteModal(null)} className="flex-1 btn-outline text-center cursor-pointer">Vazgeç</button>
              <button onClick={handleDelete} className="flex-1 bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-600 cursor-pointer">Sil</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit / Detail modal */}
      {showEditModal && editingDelivery && (
        <div className="fixed inset-0 bg-black/40 z-[100] flex items-start justify-center pt-[8vh]" onClick={() => setShowEditModal(null)}>
          <div className="bg-background-50 rounded-2xl border border-background-200 shadow-xl w-full max-w-lg max-h-[85vh] overflow-hidden mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-100">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-accent-500 flex items-center justify-center">
                  <i className="ri-truck-line text-background-50 text-sm"></i>
                </span>
                <div>
                  <h3 className="text-base font-semibold text-foreground-900">Teslimat Düzenle</h3>
                  <p className="text-xs text-foreground-400">{editingDelivery.sale_no} — {editingDelivery.customer_name}</p>
                </div>
              </div>
              <button onClick={() => setShowEditModal(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-400 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="px-5 py-4 overflow-y-auto max-h-[calc(85vh-130px)] space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Teslimat Tarihi</label>
                  <input type="date" value={editDate} onChange={e => setEditDate(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Başlangıç Saati</label>
                  <input type="time" value={editStartTime} onChange={e => setEditStartTime(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Bitiş Saati</label>
                  <input type="time" value={editEndTime} onChange={e => setEditEndTime(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Durum</label>
                  <select value={editStatus} onChange={e => setEditStatus(e.target.value as DeliveryStatus)} className={selectClass}>
                    <option value="planned">Planlandı</option>
                    <option value="on_the_way">Yolda</option>
                    <option value="delivered">Teslim Edildi</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className={labelClass}>Nakliyeci / Teslimat Personeli</label>
                  <input type="text" value={editPerson} onChange={e => setEditPerson(e.target.value)} className={inputClass} placeholder="Mehmet Nakliye" />
                </div>
                <div className="col-span-2">
                  <CityDistrictSelect
                    city={editCity}
                    district={editDistrict}
                    neighborhood={editNeighborhood}
                    onCityChange={setEditCity}
                    onDistrictChange={setEditDistrict}
                    onNeighborhoodChange={setEditNeighborhood}
                    selectClassName={selectClass}
                  />
                </div>
                <div className="col-span-2">
                  <label className={labelClass}>Teslimat Adresi</label>
                  <input type="text" value={editAddress} onChange={e => setEditAddress(e.target.value)} className={inputClass} placeholder="Açık adres (sokak, bina, daire vb.)" />
                </div>
                <div>
                  <label className={labelClass}>Müşteri</label>
                  <div className="w-full px-3 py-2 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-600">{editingDelivery.customer_name}</div>
                </div>
                <div className="col-span-2">
                  <label className={labelClass}>Teslimat Notu</label>
                  <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} maxLength={500} className={`${inputClass} resize-none h-20`} placeholder="Teslimat ile ilgili notlar..."></textarea>
                </div>
                {editingDelivery.delivered_at && (
                  <div className="col-span-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-center gap-2">
                    <i className="ri-check-double-line text-emerald-500"></i>
                    <span className="text-xs text-emerald-700">Teslim Edilme: {new Date(editingDelivery.delivered_at).toLocaleString('tr-TR')}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-background-100 bg-background-50">
              <button onClick={() => setShowEditModal(null)} className="btn-outline cursor-pointer whitespace-nowrap">İptal</button>
              <button onClick={handleEditSave} className="btn-primary flex items-center gap-2 cursor-pointer whitespace-nowrap">
                <i className="ri-check-line"></i>
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delivery Report Modal */}
      {showReportDelivery && (
        <DeliveryReport
          delivery={showReportDelivery}
          onClose={() => setShowReportDelivery(null)}
        />
      )}

      {/* Teslim Edildi Prompt */}
      {showDeliveredPrompt && (
        <div className="fixed inset-0 bg-black/40 z-[150] flex items-center justify-center p-4" onClick={() => setShowDeliveredPrompt(null)}>
          <div className="bg-background-50 rounded-2xl p-6 max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4 mx-auto">
              <i className="ri-check-double-line text-xl text-emerald-500"></i>
            </div>
            <h3 className="text-lg font-semibold text-foreground-900 text-center mb-2">Teslimat Tamamlandı</h3>
            <p className="text-sm text-foreground-500 text-center mb-2">
              <strong>{showDeliveredPrompt.customer_name}</strong> müşterisine teslimat başarıyla tamamlandı olarak işaretlenecek.
            </p>
            <p className="text-sm text-foreground-400 text-center mb-6">
              Teslimat tutanağını yazdırmak ister misiniz?
            </p>
            <div className="flex flex-col gap-2">
              <button onClick={confirmDeliveredAndPrint} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-printer-line"></i>
                Evet, Tutanağı Yazdır
              </button>
              <button onClick={confirmMarkDelivered} className="w-full px-4 py-2.5 border border-background-200 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                Hayır, Sadece İşaretle
              </button>
              <button onClick={() => setShowDeliveredPrompt(null)} className="w-full px-4 py-2.5 text-foreground-400 text-sm hover:text-foreground-600 transition-colors cursor-pointer whitespace-nowrap">
                Vazgeç
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}